<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { parseISO } from 'date-fns';
import { ChevronRight } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { SearchInput } from '@/components/ui/input';
import Spinner from '@/components/ui/Spinner.vue';
import { availabilityOf, countDatesByMonth, hasBothDates, isDateSelectionComplete, reportRequestOf, toggleCountDate, type ReportDateSelection } from '@/composables/inventory/inventoryReport';
import { i18n } from '@/plugins/internationalization/i18n';
import { useMedicineCountDates } from '@/plugins/store/actions/queries/inventory';
import type { MedicineCountDatesFarm, MedicineReportRequestFarm } from '@/types';

/** From this many farms the list gets a search field; below it every farm is in view anyway. */
const SEARCH_FROM_FARMS = 8;

const props = defineProps<{ farmIds: number[] }>();

const selection = defineModel<ReportDateSelection>('value', { required: true });

const emit = defineEmits<{ next: [farms: MedicineReportRequestFarm[]]; back: [] }>();

const { data, isLoading, isError, refetch } = useMedicineCountDates(toRef(props, 'farmIds'));

const farms = computed(() => data.value ?? []);
const canContinue = computed(() => isDateSelectionComplete(farms.value, selection.value));

const search = ref('');
const visibleFarms = computed(() => {
    const query = search.value.trim().toLocaleLowerCase();

    return query === '' ? farms.value : farms.value.filter((farm) => farm.farm_name.toLocaleLowerCase().includes(query));
});

function isPending(farm: MedicineCountDatesFarm): boolean {
    return availabilityOf(farm) === 'comparable' && !hasBothDates(selection.value, farm.farm_id);
}

/** The farm whose count days the card on the right shows. */
const activeFarmId = ref<number | null>(null);
const activeFarm = computed(() => farms.value.find((farm) => farm.farm_id === activeFarmId.value) ?? null);
const activeMonths = computed(() => (activeFarm.value ? countDatesByMonth(activeFarm.value.dates) : []));

// Nothing is picked up front; the card opens on the first farm still waiting for its days. Days picked before going back are kept.
watch(
    farms,
    (loaded) => {
        if (!loaded.some((farm) => farm.farm_id === activeFarmId.value)) {
            activeFarmId.value = (loaded.find(isPending) ?? loaded[0])?.farm_id ?? null;
        }
    },
    { immediate: true }
);

/** The next farm after the open one that still lacks its days, wrapping round; none once every farm has them. */
const nextFarm = computed(() => {
    const list = farms.value;
    const index = list.findIndex((farm) => farm.farm_id === activeFarmId.value);

    for (let offset = 1; offset < list.length; offset++) {
        const farm = list[(index + offset) % list.length];

        if (isPending(farm)) {
            return farm;
        }
    }

    return null;
});

/** The farm's line in the list: its period once both days are picked, otherwise what it is missing. */
function statusOf(farm: MedicineCountDatesFarm): { text: string; tone: string } {
    const availability = availabilityOf(farm);

    if (availability === 'single') {
        return { text: i18n.global.t('views.health.inventory.report.selectDates.onlyOneCount'), tone: 'text-gray-400' };
    }
    if (availability === 'none') {
        return { text: i18n.global.t('views.health.inventory.report.selectDates.noCount'), tone: 'text-gray-400' };
    }
    if (!hasBothDates(selection.value, farm.farm_id)) {
        return { text: i18n.global.t('views.health.inventory.report.selectDates.missing'), tone: 'text-warning-700' };
    }

    const [start, end] = selection.value[farm.farm_id];

    return { text: `${formatDate(start)} → ${formatDate(end)}`, tone: 'text-gray-700' };
}

function isPicked(farmId: number, date: string): boolean {
    return (selection.value[farmId] ?? []).includes(date);
}

/** A third day cannot be ticked while two are; unticking one frees the choice. */
function isLocked(farmId: number, date: string): boolean {
    return hasBothDates(selection.value, farmId) && !isPicked(farmId, date);
}

/** Which end of the period a picked day is, once both are picked: the earlier starts it. */
function roleOf(farmId: number, date: string): 'start' | 'end' | null {
    if (!hasBothDates(selection.value, farmId) || !isPicked(farmId, date)) {
        return null;
    }

    return selection.value[farmId][0] === date ? 'start' : 'end';
}

function toggle(farmId: number, date: string) {
    selection.value = { ...selection.value, [farmId]: toggleCountDate(selection.value[farmId] ?? [], date) };
}

/** Hands over the farms that can be compared, each with its two days. */
function show() {
    emit('next', reportRequestOf(farms.value, selection.value));
}

function formatDate(date: string): string {
    return i18n.global.d(parseISO(date), 'short');
}

function formatMonth(month: string): string {
    return i18n.global.d(parseISO(`${month}-01`), 'monthOfYearLong');
}
</script>

<template>
    <div class="w-full space-y-4">
        <h2 class="font-semibold text-xl text-center">{{ $t('views.health.inventory.report.selectDates.title') }}</h2>
        <h3 class="text-center font-semibold">{{ $t('views.health.inventory.report.selectDates.description') }}</h3>

        <div v-if="isLoading" class="flex items-center justify-center gap-2 py-10">
            <div class="font-medium">{{ $t('views.health.inventory.report.loading') }}</div>
            <Spinner class="size-5 animate-spin" />
        </div>
        <div v-else-if="isError" class="py-10 text-center space-y-4">
            <p class="text-sm text-red-600">{{ $t('views.pigs.records.errors.couldNotLoadData') }}</p>
            <Button variant="outline" @click="refetch()">{{ $t('views.pigs.records.errors.retry') }}</Button>
        </div>
        <!-- The farms on the left, each with the period picked for it; the open farm's count days on the right. -->
        <div v-else class="grid gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div class="flex flex-col rounded-lg border-2 border-black border-opacity-10">
                <div v-if="farms.length >= SEARCH_FROM_FARMS" class="border-b border-gray-200 p-2">
                    <SearchInput v-model="search" :placeholder="$t('views.health.inventory.report.selectDates.searchPlaceholder')" />
                </div>
                <ul class="max-h-[32rem] overflow-y-auto divide-y divide-gray-100">
                    <li v-for="farm in visibleFarms" :key="farm.farm_id">
                        <button
                            type="button"
                            class="flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left"
                            :class="farm.farm_id === activeFarmId ? 'bg-gray-100' : 'hover:bg-gray-50'"
                            :aria-current="farm.farm_id === activeFarmId ? 'true' : undefined"
                            @click="activeFarmId = farm.farm_id"
                        >
                            <span class="truncate font-medium" :class="{ 'text-gray-400': availabilityOf(farm) !== 'comparable' }">{{ farm.farm_name }}</span>
                            <span class="whitespace-nowrap text-sm tabular-nums" :class="statusOf(farm).tone">{{ statusOf(farm).text }}</span>
                        </button>
                    </li>
                    <li v-if="visibleFarms.length === 0" class="px-3 py-6 text-center text-sm text-gray-500">{{ $t('views.health.inventory.report.selectDates.noMatch') }}</li>
                </ul>
            </div>

            <div v-if="activeFarm" class="flex flex-col gap-4 rounded-lg border-2 border-black border-opacity-10 p-4">
                <div class="flex flex-wrap items-baseline justify-between gap-2">
                    <h4 class="font-semibold">{{ activeFarm.farm_name }}</h4>
                    <span v-if="availabilityOf(activeFarm) === 'comparable'" class="text-sm text-gray-500">{{ $t('views.health.inventory.report.selectDates.pickTwo') }}</span>
                </div>
                <p v-if="availabilityOf(activeFarm) === 'single'" class="text-sm text-gray-500">{{ $t('views.health.inventory.report.selectDates.onlyOneCount') }}</p>
                <p v-else-if="availabilityOf(activeFarm) === 'none'" class="text-sm text-gray-500">{{ $t('views.health.inventory.report.selectDates.noCount') }}</p>
                <div v-else class="max-h-[28rem] space-y-3 overflow-y-auto">
                    <div v-for="month in activeMonths" :key="month.month" class="space-y-1.5">
                        <div class="text-xs font-medium uppercase tracking-wide text-gray-500">{{ formatMonth(month.month) }}</div>
                        <div class="flex flex-wrap gap-2">
                            <label
                                v-for="date in month.dates"
                                :key="date"
                                class="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5"
                                :class="isLocked(activeFarm.farm_id, date) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-gray-50'"
                            >
                                <Checkbox :checked="isPicked(activeFarm.farm_id, date)" :disabled="isLocked(activeFarm.farm_id, date)" @update:checked="toggle(activeFarm.farm_id, date)" />
                                <span class="tabular-nums">{{ formatDate(date) }}</span>
                                <span v-if="roleOf(activeFarm.farm_id, date)" class="text-xs text-gray-500">{{ $t(`views.health.inventory.report.selectDates.${roleOf(activeFarm.farm_id, date)}`) }}</span>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="mt-auto flex justify-end">
                    <Button variant="outline" size="sm" :disabled="nextFarm === null" @click="activeFarmId = nextFarm?.farm_id ?? activeFarmId">
                        {{ $t('views.health.inventory.report.selectDates.nextFarm') }}
                        <ChevronRight class="ml-1 size-4" aria-hidden="true" />
                    </Button>
                </div>
            </div>
        </div>
        <div class="flex justify-center gap-x-2">
            <Button variant="outline" class="min-w-32" @click="emit('back')">{{ $t('views.health.inventory.report.back') }}</Button>
            <Button :disabled="!canContinue" class="min-w-32" @click="show">{{ $t('views.health.inventory.report.show') }}</Button>
        </div>
    </div>
</template>
